import React, { useCallback, useId, useRef } from "react";
import { useAnimationFrame, usePrefersReducedMotion } from "components/emptyState/glyphMotion";

/**
 * Ported from the temp worker app (jobbiz_app_v2, components/ui/CheckList.js): conditions or
 * benefits, each under the Jobbiz badge (the orange rosette with its check). Other badges of the
 * app (eye, sparkles…) can join BADGES when a page needs them. A glint sweeps the badges one after the other, then the list rests. "Reduce motion":
 * no glint.
 *
 * items: [{ text, badge? }], `badge` a key of BADGES (the checked rosette by default).
 */

/** One cycle: the sweeps one after the other, then the rest. */
const SHIMMER_CYCLE = 3200;
/** Crossing of one badge, and the gap between two badges (fractions of the cycle). */
const SHIMMER_SWEEP = 0.22;
const SHIMMER_STAGGER = 0.12;

/** The Glint measures on the 24 square of the badge: a band 2.2 times wider, tilted by 10°. */
const TILT = (10 * Math.PI) / 180;
const REACH_X = 24 * 1.1 * Math.cos(TILT);
const REACH_Y = 24 * 1.1 * Math.sin(TILT);
const FROM = -0.2 * 24;
const TO = 1.2 * 24;

const ORANGE = "#FF9600";

const ROSETTE =
    "M9.72713 2.84281C10.8921 1.38573 13.1079 1.38573 14.2729 2.84281C14.8963 3.62252 15.8758 4.02827 16.8679 3.91773C18.722 3.71117 20.2888 5.27799 20.0823 7.13205C19.9717 8.12419 20.3775 9.10374 21.1572 9.72713C22.6143 10.8921 22.6143 13.1079 21.1572 14.2729C20.3775 14.8963 19.9717 15.8758 20.0823 16.8679C20.2888 18.722 18.722 20.2888 16.8679 20.0823C15.8758 19.9717 14.8963 20.3775 14.2729 21.1572C13.1079 22.6143 10.8921 22.6143 9.72713 21.1572C9.10374 20.3775 8.12419 19.9717 7.13205 20.0823C5.27799 20.2888 3.71117 18.722 3.91773 16.8679C4.02827 15.8758 3.62252 14.8963 2.84281 14.2729C1.38573 13.1079 1.38573 10.8921 2.84281 9.72713C3.62252 9.10374 4.02827 8.12419 3.91773 7.13205C3.71117 5.27799 5.27799 3.71117 7.13205 3.91773C8.12419 4.02827 9.10374 3.62252 9.72713 2.84281Z";

/** Orange shape (followed by the glint), its optional stroke, the white mark, the scale. */
const BADGES = {
    check: { shape: ROSETTE, mark: { d: "M8 12.05L10.8 14.85L16 9.65", width: 1.6 } },
};

/** Gradient bounds of a badge's glint at `clock` (0..1), during its window `at`. */
const shinePosition = (clock, at) => {
    const t = Math.min(Math.max((clock - at[0]) / (at[1] - at[0]), 0), 1);
    const core = FROM + (TO - FROM) * t * t * (3 - 2 * t);
    return { x1: core - REACH_X, x2: core + REACH_X };
};

const CheckBadge = ({ kind = "check", size, gradientRef, shimmer }) => {
    const { shape, outline, mark, scale = 1 } = BADGES[kind] ?? BADGES.check;
    // useId yields characters that are not allowed in a `url(#…)` reference.
    const id = `shine${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
    const edge = outline ? { strokeWidth: outline, strokeLinecap: "round", strokeLinejoin: "round" } : {};
    // Scaling the drawing down is widening, around its centre, the box that frames it.
    const side = 24 / scale;
    const origin = 12 - side / 2;
    const start = shinePosition(0, [0, 1]);

    return (
        <svg width={size} height={size} viewBox={`${origin} ${origin} ${side} ${side}`} fill="none" aria-hidden="true" className="shrink-0">
            <path d={shape} fill={ORANGE} stroke={outline ? ORANGE : undefined} {...edge} />
            {shimmer ? (
                <>
                    <defs>
                        <linearGradient
                            ref={gradientRef}
                            id={id}
                            gradientUnits="userSpaceOnUse"
                            x1={start.x1}
                            x2={start.x2}
                            y1={12 - REACH_Y}
                            y2={12 + REACH_Y}
                        >
                            <stop offset="0.42" stopColor="#FFFFFF" stopOpacity={0} />
                            <stop offset="0.5" stopColor="#FFFFFF" stopOpacity={0.6} />
                            <stop offset="0.58" stopColor="#FFFFFF" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <path d={shape} fill={`url(#${id})`} stroke={outline ? `url(#${id})` : undefined} {...edge} />
                </>
            ) : null}
            {mark ? (
                <path d={mark.d} stroke="#FFFFFF" strokeWidth={mark.width} strokeLinecap="round" strokeLinejoin="round" />
            ) : null}
        </svg>
    );
};

const CheckList = ({ items, size = 28, className = "" }) => {
    const reducedMotion = usePrefersReducedMotion();
    const gradientRefs = useRef([]);

    const render = useCallback(
        (elapsed) => {
            const clock = (elapsed / SHIMMER_CYCLE) % 1;
            gradientRefs.current.forEach((gradient, index) => {
                if (!gradient) return;
                const at = [index * SHIMMER_STAGGER, index * SHIMMER_STAGGER + SHIMMER_SWEEP];
                const { x1, x2 } = shinePosition(clock, at);
                gradient.setAttribute("x1", x1);
                gradient.setAttribute("x2", x2);
            });
            return true;
        },
        []
    );

    useAnimationFrame(render, !reducedMotion);

    return (
        <ul className={`flex flex-col gap-3 ${className}`}>
            {items.map((item, index) => (
                <li key={item.text} className="flex items-center gap-3 text-sm leading-5 text-gray-700">
                    <CheckBadge
                        kind={item.badge}
                        size={size}
                        shimmer={!reducedMotion}
                        gradientRef={(node) => (gradientRefs.current[index] = node)}
                    />
                    {item.text}
                </li>
            ))}
        </ul>
    );
};

/** One badge on its own, its glint sweeping it once per cycle (e.g. the "mission created" rosette). */
export const ShimmeringCheckBadge = ({ size = 28 }) => {
    const reducedMotion = usePrefersReducedMotion();
    const gradientRef = useRef(null);

    const render = useCallback((elapsed) => {
        if (!gradientRef.current) return true;
        const { x1, x2 } = shinePosition((elapsed / SHIMMER_CYCLE) % 1, [0, SHIMMER_SWEEP * 1.5]);
        gradientRef.current.setAttribute("x1", x1);
        gradientRef.current.setAttribute("x2", x2);
        return true;
    }, []);

    useAnimationFrame(render, !reducedMotion);

    return <CheckBadge size={size} shimmer={!reducedMotion} gradientRef={gradientRef} />;
};

export default CheckList;
